import React from 'react';
import { useEffect } from 'react';

import Page from '../components/Page';
import OverviewBox from '../components/OverviewBox';
import SpiderChart from '../assets/SpiderChart';
import { css } from '@emotion/css';
import { Localized, useLanguage } from '../components/Localized';

/**
 * @brief RenderComponent that renders the overview page of the assessment results
 * @param {Object} props.assessmentData contains spider scores and questions texts
 * @param {String} props.overviewTitle contains the title of the second paragraph
 * @param {Array} props.overviewInfo contains and array with the information to display a summary of each assessment dimensoin
 */
const ResultsOverviewPage = (props) => {
    const lang = useLanguage();

    /* Calculate overall scores and resulting "Reifegrad" */
    const averageScores = _calculate_overall_score(props.assessmentData);

    let scoreLevel;

    if (averageScores.oUserScore <= 1) {
        scoreLevel = 'Traditional';
    } else if (averageScores.oUserScore <= 2) {
        scoreLevel = 'Emerging';
    } else if (averageScores.oUserScore <= 3.5) {
        scoreLevel = 'Advanced';
    } else if (averageScores.oUserScore <= 4) {
        scoreLevel = 'Excellence';
    } else {
        scoreLevel = '';
    }

    const legendData = [
        {
            label: <Localized de="Ihr Gesamtscore: " en="Your total score: " />,
            score: averageScores.oUserScore.toFixed(1),
            color: 'rgb(209, 0, 25)',
        },
        {
            label: <Localized de="Ø Score: " en="Ø score: " />,
            score: averageScores.oAvgScore.toFixed(1),
            color: 'rgb(255, 161, 0)',
        },
        {
            label: <Localized de="Bester Score: " en="Best score: " />,
            score: averageScores.oBestScore.toFixed(1),
            color: 'rgb(58,174,121)',
        },
    ];

    const scoreList = legendData.map((legendEntry) => (
        <div
            key={legendEntry.color}
            className={css({
                display: 'flex',
                alignItems: 'center',
                '& > span': {
                    marginRight: '5px',
                    width: '1em',
                    height: '1em',
                },
            })}
        >
            <span style={{ backgroundColor: legendEntry.color }} />
            {legendEntry.label}
            {legendEntry.score}/4
        </div>
    ));

    /* Initiate Spider Draw after rest of page has fully loaded*/
    useEffect(() => {
        // Update the document title using the browser API
        let options = {
            data: { categories: props.assessmentData.data },
            element: 'spiderOverview',
            language: lang,
        };
        let spider = new SpiderChart(options);
    });

    /* Render overview text for each dimension */
    const dimensionList = props.overviewInfo.map((dimensionInfo) => (
        <OverviewBox key={dimensionInfo.icon} title={<Localized {...dimensionInfo.title} />} icon={dimensionInfo.icon}>
            <Localized {...dimensionInfo.text} />
        </OverviewBox>
    ));

    return (
        <Page>
            <h1>
                <Localized de="Gesamtauswertung" en="Results overview" />
            </h1>
            <p>
                <Localized
                    de="Ihre Ausprägung nach dem Reifegradmodell: "
                    en="Your maturity level according to the excellence model: "
                />
                <b>{scoreLevel}</b>
            </p>
            <div
                className={css({
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingBottom: '2mm',
                })}
            >
                {scoreList}
            </div>
            <div
                className={css({
                    fontSize: '8pt',
                    paddingBottom: '1em',
                })}
            >
                <Localized
                    de="Bewertungsmethode: Der Ø Score (orange) entspricht dem Durchschnitt aller Teilnehmer pro Unterkategorie. Der beste Score (grün) entspricht dem Score des Teilnehmers mit dem besten Gesamtscore."
                    en="Methodology: The Ø score (orange) represents the average score of all participants per sub category. The best score (green) represents the score of the participant with the best total score."
                />
            </div>
            <div
                className={css({
                    width: '55%',
                    textAlign: 'center',
                    margin: '0 auto',
                    paddingBottom: '2mm',
                })}
                id="spiderOverview"
            />
            <h1>
                <Localized {...props.overviewTitle} />
            </h1>
            {dimensionList}
        </Page>
    );
};

/**
 * @brief Helper function to calculate the average scores of each results category (user, average and benchmark-scores)
 * @param {Object} inputData contains the survey results as a javascript-object
 */
const _calculate_overall_score = (inputData) => {
    // Calculates the overall user score
    let overallUserScore = 0;
    let overallAvgScore = 0;
    let overallBestScore = 0;
    let iter = 0;

    for (let category of inputData.data) {
        for (let subcategory of category.subcatArray) {
            overallUserScore += subcategory.subcatUserScore;
            overallAvgScore += subcategory.subcatAvgScore;
            overallBestScore += subcategory.subcatBestScore;
            iter++;
        }
    }
    return {
        oUserScore: overallUserScore / iter,
        oAvgScore: overallAvgScore / iter,
        oBestScore: overallBestScore / iter,
    };
};

export default ResultsOverviewPage;
